import { createSignal } from "solid-js";
import { accountIdForAlias } from "./account-alias";

export const TAB_ACCOUNT_STORAGE_KEY = "fieldcheck_tab_account";
export const LAST_ACTIVE_ACCOUNT_KEY = "fieldcheck_last_active_account";
export const TAB_ACCOUNT_CHANGED_EVENT = "fieldcheck:tab-account-changed";
export const ACCOUNT_HANDOFF_PARAM = "as";

const [tabAccountSignal, setTabAccountSignal] = createSignal<string | null>(
  readTabAccountIdInitial(),
);

if (typeof globalThis.addEventListener === "function") {
  globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, () => {
    setTabAccountSignal(readTabAccountId());
  });
}

let handoffConsumed = false;

function readTabAccountIdInitial(): string | null {
  try {
    if (typeof window === "undefined") {
      return null;
    }

    if (!handoffConsumed) {
      handoffConsumed = true;
      const searchParams = new URLSearchParams(window.location.search);
      const alias = searchParams.get("as");
      if (alias) {
        searchParams.delete("as");
        const cleanSearch = searchParams.toString();
        const newUrl =
          window.location.pathname + (cleanSearch ? `?${cleanSearch}` : "") + window.location.hash;
        window.history.replaceState(null, "", newUrl);

        const targetUserId = accountIdForAlias(alias);
        if (targetUserId) {
          try {
            window.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, targetUserId);
          } catch {
            // Storage unavailable
          }
          return targetUserId;
        }
      }
    }

    const currentTab = window.sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY);
    if (currentTab) {
      return currentTab;
    }
    return null;
  } catch {
    return null;
  }
}

export function readTabAccountId(): string | null {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return window.sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function readLastActiveAccountId(): string | null {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return window.localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY);
  } catch {
    return null;
  }
}

export function activeTabAccountId(): string | null {
  return tabAccountSignal() ?? readTabAccountId();
}

export interface WriteTabAccountOptions {
  silent?: boolean;
}

export function writeTabAccountId(userId: string, options?: WriteTabAccountOptions): void {
  try {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, userId);
      window.localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, userId);
    }
  } catch {
    // Storage unavailable
  }

  setTabAccountSignal(userId);

  if (!options?.silent) {
    notifyTabAccountChanged();
  }
}

export function notifyTabAccountChanged(): void {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(TAB_ACCOUNT_CHANGED_EVENT));
    }
  } catch {
    // Ignore error
  }
}

export function clearTabAccountId(): void {
  try {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(TAB_ACCOUNT_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable
  }
  setTabAccountSignal(null);
  notifyTabAccountChanged();
}
